// src/pages/partner_management/PartnerTable.tsx
//
// Partner Overview table (EE-272). Presentational: the page owns the query
// (search, filters, page, sort) and hands the rows down, the same split as
// RequisitionListUI/RequisitionTable. The table used to fetch for itself off a
// `refreshTrigger` counter and had no search to drive.
//
// Sort keys are the names PartnerController.SORTABLE_COLUMNS whitelists; an
// unknown key silently sorts by id, so keep the two lists in step.
import { FunctionComponent } from "react";
import {
  Paper,
  LinearProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import DashboardTableHeadCell, {
  DashboardTableRefreshCell,
} from "components/Dashboard/DashboardTableHeadCell";
import { SKELETON_ROW_HEIGHT } from "components/Dashboard/dashboardTable";
import { PartnerSortKey, PartnerType } from "types/partners/PartnerTypes";
import PartnerRow from "./PartnerRow";

// Name, phone, email, company, address, service type, four EE-240 history
// columns, actions, refresh.
const PARTNER_COLUMN_COUNT = 12;

type PropsType = {
  partners: PartnerType[];
  total: number;
  isFetching: boolean;
  page: number;
  pageSize: number;
  sortKey: PartnerSortKey;
  sortAsc: boolean;
  onSortChange: (key: PartnerSortKey) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onEdit: (partner: PartnerType) => void;
  onDelete: (partner: PartnerType) => void;
  onRefresh: () => void;
};

const PartnerTable: FunctionComponent<PropsType> = ({
  partners,
  total,
  isFetching,
  page,
  pageSize,
  sortKey,
  sortAsc,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
  onRefresh,
}) => {
  // Direction only for the active column: MUI keeps the inactive arrow on
  // hover, and it should point down, since the first click sorts descending.
  const sortProps = (key: PartnerSortKey) => ({
    onSort: () => onSortChange(key),
    sortActive: sortKey === key,
    sortDirection: (sortKey === key && sortAsc ? "asc" : "desc") as
      | "asc"
      | "desc",
  });

  // Only the very first load shows skeletons; with keepPreviousData the rows
  // stay put during a refetch and the progress bar says something is coming.
  const initialLoad = isFetching && partners.length === 0 && total === 0;

  return (
    <Paper sx={{ width: "100%" }}>
      {isFetching ? <LinearProgress /> : null}
      <TableContainer>
        <Table>
          <TableHead sx={{ background: "var(--header)" }}>
            <TableRow>
              <DashboardTableHeadCell {...sortProps("name")}>
                Name
              </DashboardTableHeadCell>
              <DashboardTableHeadCell>Phone.nr.</DashboardTableHeadCell>
              <DashboardTableHeadCell {...sortProps("email")}>
                Email
              </DashboardTableHeadCell>
              <DashboardTableHeadCell {...sortProps("company")}>
                Company
              </DashboardTableHeadCell>
              <DashboardTableHeadCell>Address</DashboardTableHeadCell>
              <DashboardTableHeadCell {...sortProps("templateType")}>
                Service Type description
              </DashboardTableHeadCell>
              {/* EE-240: history off the partner's newest inquiry */}
              <DashboardTableHeadCell>Last items total</DashboardTableHeadCell>
              <DashboardTableHeadCell>Last transport costs</DashboardTableHeadCell>
              <DashboardTableHeadCell>Last delivery time</DashboardTableHeadCell>
              <DashboardTableHeadCell>Previous comments / notes</DashboardTableHeadCell>
              <DashboardTableHeadCell>{""}</DashboardTableHeadCell>
              <DashboardTableRefreshCell onRefresh={onRefresh} disabled={isFetching} />
            </TableRow>
          </TableHead>
          {initialLoad ? (
            <TableBody>
              {Array.from({ length: pageSize }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={PARTNER_COLUMN_COUNT}>
                    <Skeleton variant="rectangular" height={SKELETON_ROW_HEIGHT} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          ) : partners.length > 0 ? (
            <TableBody>
              {partners.map((partner) => (
                <PartnerRow
                  key={partner.id}
                  partner={partner}
                  onEdit={() => onEdit(partner)}
                  onDelete={() => onDelete(partner)}
                />
              ))}
            </TableBody>
          ) : (
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={PARTNER_COLUMN_COUNT}
                  style={{ textAlign: "center" }}
                >
                  No partners found
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={total}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(e) => onPageSizeChange(+e.target.value)}
      />
    </Paper>
  );
};

export default PartnerTable;
